import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Bricolage+Grotesque:wght@700;800&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg: #04060f;
    --border: rgba(255,255,255,.07);
    --border2: rgba(255,255,255,.14);
    --text: rgba(255,255,255,.92);
    --sub: rgba(255,255,255,.48);
    --indigo: #6366f1;
    --cyan: #22d3ee;
    --green: #34d399;
    --amber: #fbbf24;
    --fh: 'Bricolage Grotesque', system-ui, sans-serif;
    --fb: 'Inter', system-ui, sans-serif;
    --ease: cubic-bezier(.22,1,.36,1);
  }

  .p {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px 16px;
    font-family: var(--fb);
    color: var(--text);
    background: var(--bg);
    position: relative;
    overflow: hidden;
  }

  /* ── Orbs ── */
  .orb { position: fixed; border-radius: 50%; pointer-events: none; filter: blur(90px); z-index: 0; }
  .o1 { width:600px;height:600px;top:-220px;left:-180px;background:radial-gradient(circle,rgba(99,102,241,.3),transparent 65%);animation:o1 20s ease-in-out infinite; }
  .o2 { width:500px;height:500px;top:-160px;right:-140px;background:radial-gradient(circle,rgba(6,182,212,.22),transparent 65%);animation:o2 16s ease-in-out infinite; }
  .o3 { width:700px;height:600px;bottom:-280px;left:50%;transform:translateX(-50%);background:radial-gradient(circle,rgba(99,102,241,.16),transparent 65%);animation:o3 24s ease-in-out infinite; }
  @keyframes o1 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(30px,-20px)} }
  @keyframes o2 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(-24px,18px)} }
  @keyframes o3 { 0%,100%{transform:translateX(-50%) scale(1)} 50%{transform:translateX(-50%) scale(1.06)} }

  /* ── Grid ── */
  .grid-bg {
    position:fixed;inset:0;pointer-events:none;z-index:0;
    background-image:linear-gradient(rgba(255,255,255,.022) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.022) 1px,transparent 1px);
    background-size:44px 44px;
    mask-image:radial-gradient(ellipse 85% 75% at 50% 40%,black,transparent);
  }

  /* ── Cursor ── */
  .cursor-glow {
    position:fixed;width:500px;height:500px;border-radius:50%;
    pointer-events:none;z-index:0;
    background:radial-gradient(circle,rgba(99,102,241,.06),transparent 60%);
    transform:translate(-50%,-50%);
    transition:left .12s linear,top .12s linear;
  }

  /* ── Particules ── */
  .particle {
    position: fixed;
    pointer-events: none;
    z-index: 999;
    border-radius: 50%;
    animation: particleFly var(--pdur, .8s) var(--ease) forwards;
  }
  @keyframes particleFly {
    0%   { opacity:1; transform: translate(0,0) scale(1); }
    100% { opacity:0; transform: translate(var(--px,0px), var(--py,-60px)) scale(0); }
  }

  /* ── Floating Icons ── */
  .floats {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 2;
    overflow: hidden;
  }
  .fi {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 14px;
    border: 1px solid rgba(255,255,255,.12);
    background: rgba(255,255,255,.06);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    box-shadow: 0 4px 20px rgba(0,0,0,.4), 0 1px 0 rgba(255,255,255,.08) inset;
    font-size: 20px;
    opacity: 0;
    animation: floatUp var(--dur, 12s) var(--delay, 0s) ease-in-out infinite;
    will-change: transform, opacity;
  }
  .fi.sm { width:40px; height:40px; border-radius:12px; font-size:17px; }
  .fi.md { width:50px; height:50px; border-radius:14px; font-size:21px; }
  .fi.lg { width:62px; height:62px; border-radius:16px; font-size:26px; }
  .fi.ind { border-color:rgba(99,102,241,.4);  background:rgba(99,102,241,.15); box-shadow:0 4px 24px rgba(99,102,241,.3),0 1px 0 rgba(255,255,255,.1) inset; }
  .fi.cyn { border-color:rgba(34,211,238,.35); background:rgba(34,211,238,.12); box-shadow:0 4px 24px rgba(34,211,238,.25),0 1px 0 rgba(255,255,255,.1) inset; }
  .fi.grn { border-color:rgba(52,211,153,.35); background:rgba(52,211,153,.12); box-shadow:0 4px 24px rgba(52,211,153,.25),0 1px 0 rgba(255,255,255,.1) inset; }
  .fi.amb { border-color:rgba(251,191,36,.32);  background:rgba(251,191,36,.11); box-shadow:0 4px 24px rgba(251,191,36,.22),0 1px 0 rgba(255,255,255,.1) inset; }
  @keyframes floatUp {
    0%   { opacity:0;  transform: translateY(30px) rotate(var(--r0,-4deg)) scale(.85); }
    15%  { opacity: var(--op, .7); }
    50%  { opacity: var(--op, .7); transform: translate(var(--mx,10px), var(--my,-50px)) rotate(var(--r1,4deg)) scale(1.02); }
    85%  { opacity: var(--op, .7); }
    100% { opacity:0;  transform: translate(var(--mx2,-6px),-100px) rotate(var(--r2,-3deg)) scale(.88); }
  }

  /* ── Shell ── */
  .sh { width:100%; max-width:980px; position:relative; z-index:3; opacity:0; transform:translateY(18px); transition:opacity 600ms var(--ease),transform 600ms var(--ease); }
  .sh.in { opacity:1; transform:translateY(0); }

  /* ── Topbar ── */
  .tb { display:flex;align-items:center;justify-content:space-between;padding:10px 16px;border-radius:16px;border:1px solid var(--border);background:rgba(255,255,255,.035);backdrop-filter:blur(24px);-webkit-backdrop-filter:blur(24px);box-shadow:0 1px 0 rgba(255,255,255,.05) inset;margin-bottom:14px; }
  .tb-left { display:flex;align-items:center;gap:12px; }
  .tb-logo { width:38px;height:38px;border-radius:11px;background:linear-gradient(135deg,rgba(99,102,241,.35),rgba(6,182,212,.2));border:1px solid rgba(255,255,255,.1);display:flex;align-items:center;justify-content:center;box-shadow:0 0 18px rgba(99,102,241,.2); }
  .tb-name { font-family:var(--fh);font-size:15px;font-weight:800;letter-spacing:-.02em; }
  .tb-sub  { font-size:10.5px;color:var(--sub);font-weight:500;letter-spacing:.05em;margin-top:1px; }
  .tb-right { display:flex;align-items:center;gap:10px; }
  .tb-badge { display:flex;align-items:center;gap:6px;padding:6px 13px;border-radius:999px;border:1px solid rgba(99,102,241,.25);background:rgba(99,102,241,.09);font-size:10px;font-weight:700;color:rgba(165,180,252,.85);letter-spacing:.08em;text-transform:uppercase; }
  .tb-dot { width:5px;height:5px;border-radius:50%;background:var(--indigo);box-shadow:0 0 7px rgba(99,102,241,1);animation:blink 2s ease-in-out infinite;flex-shrink:0; }
  .tb-stat { display:flex;align-items:center;gap:5px;padding:5px 10px;border-radius:999px;border:1px solid rgba(52,211,153,.2);background:rgba(52,211,153,.07);font-size:10px;font-weight:600;color:rgba(110,231,183,.8); }
  .tb-stat-dot { width:4px;height:4px;border-radius:50%;background:var(--green);box-shadow:0 0 5px rgba(52,211,153,.8);animation:blink 1.5s ease-in-out infinite; }
  @keyframes blink { 0%,100%{opacity:1} 50%{opacity:.25} }

  /* ── Panel ── */
  .panel { border-radius:24px;border:1px solid var(--border);background:linear-gradient(160deg,rgba(255,255,255,.058),rgba(255,255,255,.018));backdrop-filter:blur(28px);-webkit-backdrop-filter:blur(28px);overflow:hidden;box-shadow:0 1px 0 rgba(255,255,255,.07) inset,0 40px 100px rgba(0,0,0,.45);position:relative; }
  .panel::before { content:"";position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent 5%,rgba(99,102,241,.65) 35%,rgba(34,211,238,.5) 65%,transparent 95%); }
  .panel-glow { position:absolute;top:-160px;left:50%;transform:translateX(-50%);width:680px;height:320px;background:radial-gradient(ellipse,rgba(99,102,241,.14),transparent 65%);pointer-events:none; }

  /* ── Stats bar ── */
  .stats-bar { display:flex;justify-content:center;gap:0;border-bottom:1px solid var(--border);position:relative;z-index:1;overflow:hidden; }
  .stat-item { flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:14px 10px;border-right:1px solid var(--border);position:relative;transition:background 200ms; }
  .stat-item:last-child { border-right:none; }
  .stat-item:hover { background:rgba(255,255,255,.025); }
  .stat-num { font-family:var(--fh);font-size:22px;font-weight:800;letter-spacing:-.03em;line-height:1; }
  .stat-num.ind { background:linear-gradient(110deg,#a5b4fc,#818cf8);-webkit-background-clip:text;background-clip:text;color:transparent; }
  .stat-num.cyn { background:linear-gradient(110deg,#67e8f9,#38bdf8);-webkit-background-clip:text;background-clip:text;color:transparent; }
  .stat-num.grn { background:linear-gradient(110deg,#6ee7b7,#34d399);-webkit-background-clip:text;background-clip:text;color:transparent; }
  .stat-label { font-size:9.5px;color:var(--sub);font-weight:600;letter-spacing:.1em;text-transform:uppercase;margin-top:3px; }

  /* ── Hero top ── */
  .hero-top { padding:38px 32px 24px;text-align:center;position:relative;z-index:1; }
  .eyebrow { display:inline-flex;align-items:center;gap:8px;font-size:10px;font-weight:700;letter-spacing:.22em;text-transform:uppercase;color:rgba(165,180,252,.6);margin-bottom:16px; }
  .ey-line { width:24px;height:1px;background:linear-gradient(90deg,rgba(99,102,241,.7),transparent); }
  .ey-line.r { background:linear-gradient(270deg,rgba(34,211,238,.6),transparent); }
  .h1 { font-family:var(--fh);font-size:clamp(24px,4vw,44px);font-weight:800;letter-spacing:-.04em;line-height:1.08;margin-bottom:12px; }
  .h1-grad { background:linear-gradient(110deg,#a5b4fc 0%,#67e8f9 50%,#93c5fd 100%);-webkit-background-clip:text;background-clip:text;color:transparent; }
  .lead { font-size:14px;color:var(--sub);line-height:1.65;max-width:460px;margin:0 auto 20px; }
  .lead b { color:rgba(255,255,255,.82);font-weight:600; }

  /* ── Tabs ── */
  .tabs { display:flex;justify-content:center;gap:6px;margin-bottom:0; }
  .tab { padding:6px 14px;border-radius:999px;font-size:11px;font-weight:600;border:1px solid var(--border);background:transparent;color:var(--sub);cursor:pointer;transition:all 180ms var(--ease);font-family:var(--fb); }
  .tab:hover { border-color:var(--border2);color:rgba(255,255,255,.7); }
  .tab.active { background:rgba(99,102,241,.15);border-color:rgba(99,102,241,.35);color:rgba(165,180,252,.9); }

  /* ── Cards ── */
  .cards { display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px;padding:18px;border-top:1px solid var(--border);position:relative;z-index:1; }
  .card { border-radius:16px;border:1px solid var(--border);background:rgba(255,255,255,.038);overflow:hidden;position:relative;transition:all 240ms var(--ease);animation:cin 500ms var(--ease) both;cursor:pointer;transform-style:preserve-3d; }
  .card:nth-child(2) { animation-delay:80ms; }
  @keyframes cin { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
  .card:hover { border-color:var(--border2);background:rgba(255,255,255,.065);box-shadow:0 28px 64px rgba(0,0,0,.42); }
  .c-stripe { height:2px; }
  .cc .c-stripe { background:linear-gradient(90deg,#6366f1,#8b5cf6 60%,transparent); }
  .ce .c-stripe { background:linear-gradient(90deg,#22d3ee,#3b82f6 60%,transparent); }
  .c-glow { position:absolute;top:-60px;left:-60px;width:200px;height:200px;border-radius:50%;filter:blur(50px);opacity:.1;pointer-events:none;transition:opacity 240ms; }
  .cc .c-glow { background:rgba(99,102,241,1); }
  .ce .c-glow { background:rgba(6,182,212,1); }
  .card:hover .c-glow { opacity:.18; }
  .c-body { padding:20px 18px 16px;position:relative;z-index:1; }
  .c-head { display:flex;align-items:center;gap:12px;margin-bottom:12px; }
  .c-icon { width:44px;height:44px;border-radius:13px;display:flex;align-items:center;justify-content:center;font-size:19px;border:1px solid rgba(255,255,255,.09);flex-shrink:0;transition:transform 220ms var(--ease); }
  .cc .c-icon { background:linear-gradient(135deg,rgba(99,102,241,.22),rgba(139,92,246,.1)); }
  .ce .c-icon { background:linear-gradient(135deg,rgba(6,182,212,.22),rgba(59,130,246,.1)); }
  .card:hover .c-icon { transform:scale(1.1) rotate(-5deg); }
  .c-title { font-family:var(--fh);font-size:16px;font-weight:800;letter-spacing:-.025em;line-height:1.1; }
  .c-meta  { font-size:10px;color:var(--sub);font-weight:600;letter-spacing:.1em;text-transform:uppercase;margin-top:2px; }
  .c-sep { height:1px;background:var(--border);margin:12px 0; }
  .c-tags { display:flex;gap:5px;flex-wrap:wrap;margin-bottom:14px; }
  .c-tag {
    font-size:9.5px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;
    padding:4px 10px;border-radius:999px;border:1px solid var(--border);
    color:var(--sub);cursor:default;transition:all 180ms var(--ease);position:relative;
  }
  .c-tag:hover { border-color:var(--border2);color:rgba(255,255,255,.75);background:rgba(255,255,255,.06); }
  /* Tooltip */
  .c-tag[data-tip]:hover::after {
    content: attr(data-tip);
    position:absolute;bottom:calc(100% + 6px);left:50%;transform:translateX(-50%);
    white-space:nowrap;font-size:10px;font-weight:500;letter-spacing:0;text-transform:none;
    padding:5px 9px;border-radius:8px;
    background:rgba(15,17,30,.95);border:1px solid rgba(255,255,255,.1);
    color:rgba(255,255,255,.85);pointer-events:none;z-index:50;
    box-shadow:0 8px 24px rgba(0,0,0,.5);
  }
  .c-tag[data-tip]:hover::before {
    content:"";position:absolute;bottom:calc(100% + 1px);left:50%;transform:translateX(-50%);
    border:5px solid transparent;border-top-color:rgba(255,255,255,.1);pointer-events:none;z-index:51;
  }
  .c-acts { display:flex;gap:8px;flex-wrap:wrap;margin-bottom:12px; }
  .c-btn, .c-ghost { display:inline-flex;align-items:center;gap:6px;padding:9px 15px;border-radius:10px;font-size:12px;font-weight:700;text-decoration:none;border:1px solid transparent;transition:all 180ms var(--ease);font-family:var(--fb);white-space:nowrap;position:relative;overflow:hidden; }
  .c-btn::after { content:"";position:absolute;inset:0;background:rgba(255,255,255,0);transition:background 180ms; }
  .c-btn:hover::after { background:rgba(255,255,255,.06); }
  .c-btn:hover, .c-ghost:hover { transform:translateY(-2px); }
  .c-btn:active { transform:translateY(0) scale(.97); }
  .cc .c-btn { background:linear-gradient(135deg,#6366f1,#8b5cf6);color:#fff;box-shadow:0 6px 24px rgba(99,102,241,.35),0 1px 0 rgba(255,255,255,.14) inset; }
  .ce .c-btn { background:linear-gradient(135deg,#06b6d4,#3b82f6);color:#fff;box-shadow:0 6px 24px rgba(6,182,212,.3),0 1px 0 rgba(255,255,255,.14) inset; }
  .c-btn:hover { filter:brightness(1.12); }
  .c-ghost { background:rgba(255,255,255,.05);color:rgba(255,255,255,.68);border-color:rgba(255,255,255,.09); }
  .c-ghost:hover { background:rgba(255,255,255,.09);color:rgba(255,255,255,.9);border-color:rgba(255,255,255,.16); }

  /* Progress bar on card */
  .c-progress { margin-bottom:12px; }
  .c-progress-label { display:flex;justify-content:space-between;font-size:9.5px;color:var(--sub);font-weight:600;margin-bottom:5px;letter-spacing:.04em; }
  .c-progress-track { height:3px;border-radius:999px;background:rgba(255,255,255,.06);overflow:hidden; }
  .c-progress-fill { height:100%;border-radius:999px;width:0;transition:width 1.2s var(--ease); }
  .cc .c-progress-fill { background:linear-gradient(90deg,#6366f1,#8b5cf6); }
  .ce .c-progress-fill { background:linear-gradient(90deg,#22d3ee,#3b82f6); }
  .c-progress-fill.animate { width: var(--prog-w, 0%); }

  .c-status { display:flex;align-items:center;gap:6px; }
  .c-dot { width:6px;height:6px;border-radius:50%;flex-shrink:0;animation:pulse 2.4s ease-in-out infinite; }
  .dg { background:var(--green);box-shadow:0 0 8px rgba(52,211,153,.6); }
  .da { background:var(--amber);box-shadow:0 0 8px rgba(251,191,36,.5); }
  @keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(.75)} }
  .c-status-txt { font-size:10.5px;color:var(--sub);font-weight:500; }

  /* ── Footer nav ── */
  .fnav { display:flex;justify-content:center;align-items:center;gap:8px;flex-wrap:wrap;padding:14px 18px 16px;border-top:1px solid var(--border);position:relative;z-index:1; }
  .flink { font-size:12px;font-weight:600;color:var(--sub);text-decoration:none;transition:color 150ms;padding-bottom:1px;border-bottom:1px solid transparent; }
  .flink:hover { color:rgba(255,255,255,.88);border-bottom-color:rgba(99,102,241,.4); }
  .fsep { color:var(--border2);font-size:13px;user-select:none; }
  .arr { display:inline-flex;transition:transform 170ms; }
  .c-btn:hover .arr { transform:translateX(3px); }

  /* ── Page transition ── */
  .page-out { animation: fadeOut 300ms var(--ease) forwards; }
  @keyframes fadeOut { to { opacity:0; transform:translateY(-8px) scale(.99); } }

  @media (max-width:680px) {
    .cards { grid-template-columns:1fr; }
    .hero-top { padding:26px 18px 18px; }
    .tb-badge,.tb-stat { display:none; }
    .h1 { white-space:normal;font-size:clamp(22px,7vw,30px); }
    .fi { display:none !important; }
    .stats-bar { flex-wrap:wrap; }
    .stat-item { min-width:50%; }
  }
  @media (prefers-reduced-motion:reduce) {
    .sh,.card { transition:none;animation:none; }
    .orb { animation:none; }
    .fi { animation:none !important;opacity:0 !important; }
  }
`;

/* ── Floating Icons ── */
const ICONS = [
  { side:"left",  x:3,  top:12, dur:14, delay:0,   size:"md", color:"ind", emoji:"💼", mx:"-12px", my:"-50px", r0:"-5deg", r1:"6deg",  r2:"-3deg", op:.7  },
  { side:"left",  x:9,  top:38, dur:11, delay:2.5, size:"sm", color:"cyn", emoji:"📊", mx:"8px",   my:"-35px", r0:"4deg",  r1:"-5deg", r2:"3deg",  op:.65 },
  { side:"left",  x:5,  top:62, dur:16, delay:5,   size:"lg", color:"grn", emoji:"🎓", mx:"-8px",  my:"-60px", r0:"-6deg", r1:"5deg",  r2:"-4deg", op:.72 },
  { side:"left",  x:12, top:80, dur:10, delay:1.2, size:"sm", color:"amb", emoji:"⚡", mx:"10px",  my:"-30px", r0:"3deg",  r1:"-4deg", r2:"2deg",  op:.6  },
  { side:"left",  x:2,  top:50, dur:18, delay:7,   size:"md", color:"ind", emoji:"🔗", mx:"-14px", my:"-45px", r0:"-4deg", r1:"7deg",  r2:"-5deg", op:.6  },
  { side:"left",  x:14, top:25, dur:13, delay:3.8, size:"sm", color:"cyn", emoji:"📱", mx:"6px",   my:"-38px", r0:"5deg",  r1:"-3deg", r2:"4deg",  op:.62 },
  { side:"left",  x:7,  top:70, dur:15, delay:9,   size:"md", color:"grn", emoji:"✅", mx:"-10px", my:"-55px", r0:"-3deg", r1:"6deg",  r2:"-2deg", op:.68 },
  { side:"right", x:3,  top:18, dur:13, delay:1,   size:"md", color:"cyn", emoji:"🚀", mx:"14px",  my:"-48px", r0:"6deg",  r1:"-5deg", r2:"4deg",  op:.7  },
  { side:"right", x:8,  top:45, dur:17, delay:4,   size:"lg", color:"ind", emoji:"🧠", mx:"-10px", my:"-65px", r0:"-5deg", r1:"7deg",  r2:"-3deg", op:.72 },
  { side:"right", x:4,  top:68, dur:11, delay:2,   size:"sm", color:"amb", emoji:"💡", mx:"8px",   my:"-32px", r0:"3deg",  r1:"-4deg", r2:"3deg",  op:.64 },
  { side:"right", x:12, top:30, dur:15, delay:6.5, size:"sm", color:"grn", emoji:"📈", mx:"-8px",  my:"-40px", r0:"-4deg", r1:"5deg",  r2:"-2deg", op:.6  },
  { side:"right", x:2,  top:82, dur:12, delay:0.5, size:"md", color:"cyn", emoji:"🔐", mx:"12px",  my:"-44px", r0:"5deg",  r1:"-6deg", r2:"3deg",  op:.66 },
  { side:"right", x:10, top:55, dur:16, delay:8,   size:"sm", color:"ind", emoji:"⚙️", mx:"-6px",  my:"-36px", r0:"-3deg", r1:"4deg",  r2:"-4deg", op:.58 },
  { side:"right", x:5,  top:10, dur:14, delay:3,   size:"md", color:"amb", emoji:"🏆", mx:"10px",  my:"-52px", r0:"4deg",  r1:"-5deg", r2:"2deg",  op:.7  },
];

function FloatingIcons() {
  return (
    <div className="floats" aria-hidden="true">
      {ICONS.map((ic, i) => {
        const w = ic.size === "lg" ? 62 : ic.size === "md" ? 50 : 40;
        const leftPct = ic.side === "left"
          ? `${ic.x}%`
          : `calc(100% - ${ic.x}% - ${w}px)`;
        return (
          <div key={i} className={`fi ${ic.size} ${ic.color}`}
            style={{ left:leftPct, top:`${ic.top}%`, "--dur":`${ic.dur}s`, "--delay":`${ic.delay}s`, "--mx":ic.mx, "--my":ic.my, "--r0":ic.r0, "--r1":ic.r1, "--r2":ic.r2, "--op":ic.op }}>
            {ic.emoji}
          </div>
        );
      })}
    </div>
  );
}

/* ── Compteur animé ── */
function Counter({ target, suffix = "" }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const dur = 1400;
        const start = performance.now();
        const tick = (now) => {
          const p = Math.min((now - start) / dur, 1);
          const ease = 1 - Math.pow(1 - p, 3);
          setVal(Math.round(ease * target));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [target]);

  return <span ref={ref}>{val}{suffix}</span>;
}

/* ── Particules au clic ── */
function spawnParticles(e) {
  const colors = ["#6366f1","#22d3ee","#34d399","#fbbf24","#a5b4fc","#67e8f9"];
  const count = 10;
  for (let i = 0; i < count; i++) {
    const el = document.createElement("div");
    el.className = "particle";
    const size = Math.random() * 6 + 4;
    const angle = (Math.PI * 2 * i) / count + Math.random() * 0.5;
    const dist = Math.random() * 60 + 30;
    el.style.cssText = `
      left:${e.clientX}px; top:${e.clientY}px;
      width:${size}px; height:${size}px;
      background:${colors[Math.floor(Math.random() * colors.length)]};
      --px:${Math.cos(angle) * dist}px;
      --py:${Math.sin(angle) * dist - 20}px;
      --pdur:${Math.random() * 0.4 + 0.6}s;
    `;
    document.body.appendChild(el);
    el.addEventListener("animationend", () => el.remove(), { once: true });
  }
}

function Arr() {
  return (
    <span className="arr">
      <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="M1.5 6h9M7.5 2.5L11 6l-3.5 3.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </span>
  );
}

function Logo() {
  return (
    <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x=".5" y=".5" width="6.5" height="6.5" rx="2.2" fill="#6366f1"/>
      <rect x="9"  y=".5" width="6.5" height="6.5" rx="2.2" fill="#22d3ee" opacity=".8"/>
      <rect x=".5" y="9"  width="6.5" height="6.5" rx="2.2" fill="#3b82f6" opacity=".5"/>
      <rect x="9"  y="9"  width="6.5" height="6.5" rx="2.2" fill="#fff"    opacity=".1"/>
    </svg>
  );
}

/* ── Card avec effet 3D + progress bar ── */
function Card({ variant, onNavigate }) {
  const c = variant === "consulting";
  const cardRef = useRef(null);
  const fillRef = useRef(null);
  const animatedRef = useRef(false);

  const cfg = c
    ? {
        cls:"cc", icon:"💼", title:"HDC Consulting",
        meta:"Conseil · Transformation digitale",
        tags:[
          { label:"AMOA",   tip:"Assistance à Maîtrise d'Ouvrage" },
          { label:"ERP",    tip:"Déploiement & intégration ERP" },
          { label:"Data",   tip:"Analyse & valorisation des données" },
          { label:"Change", tip:"Conduite du changement" },
          { label:"Project Management", tip:"Getion de projet IT" },
        ],
        prog: 87, progLabel: "Taux de satisfaction",
        to1:"/consulting",         l1:"Découvrir",
        to2:"/consulting/contact", l2:"Nous contacter",
        dotCls:"dg", noteLabel:"Accès libre",
      }
    : {
        cls:"ce", icon:"🎓", title:"HDC Éducation",
        meta:"Cours · Suivi · Espace école",
        tags:[
          { label:"Cours",       tip:"Catalogue de formations" },
          { label:"Suivi",       tip:"Tableau de bord personnel" },
          { label:"Progression", tip:"Badges & certificats" },
        ],
        prog: 94, progLabel: "Apprenants actifs",
        to1:"/education", l1:"Accéder",
        to2:"/connexion", l2:"Se connecter",
        dotCls:"da", noteLabel:"Connexion requise",
      };

  /* 3D tilt */
  const handleMouseMove = useCallback((e) => {
    const el = cardRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width  - 0.5;
    const y = (e.clientY - r.top)  / r.height - 0.5;
    el.style.transform = `perspective(700px) rotateY(${x * 10}deg) rotateX(${-y * 8}deg) translateY(-6px) scale(1.02)`;
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (cardRef.current)
      cardRef.current.style.transform = "";
  }, []);

  /* Progress bar on hover */
  const handleMouseEnter = useCallback(() => {
    if (fillRef.current && !animatedRef.current) {
      animatedRef.current = true;
      fillRef.current.style.setProperty("--prog-w", `${cfg.prog}%`);
      fillRef.current.classList.add("animate");
    }
  }, [cfg.prog]);

  return (
    <article
      ref={cardRef}
      className={`card ${cfg.cls}`}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
      style={{ transition:"transform 80ms linear, border-color 240ms, background 240ms, box-shadow 240ms" }}
    >
      <div className="c-stripe"/>
      <div className="c-glow" aria-hidden="true"/>
      <div className="c-body">
        <div className="c-head">
          <div className="c-icon">{cfg.icon}</div>
          <div>
            <h2 className="c-title">{cfg.title}</h2>
            <p className="c-meta">{cfg.meta}</p>
          </div>
        </div>
        <div className="c-sep"/>
        <div className="c-tags">
          {cfg.tags.map(t => (
            <span key={t.label} className="c-tag" data-tip={t.tip}>{t.label}</span>
          ))}
        </div>

        {/* Barre de progression */}
        <div className="c-progress">
          <div className="c-progress-label">
            <span>{cfg.progLabel}</span>
            <span>{cfg.prog}%</span>
          </div>
          <div className="c-progress-track">
            <div ref={fillRef} className="c-progress-fill" style={{"--prog-w":`${cfg.prog}%`}}/>
          </div>
        </div>

        <div className="c-acts">
          <a
            href={cfg.to1}
            className="c-btn"
            onClick={e => { e.preventDefault(); spawnParticles(e); onNavigate(cfg.to1); }}
          >{cfg.l1}<Arr/></a>
          <a
            href={cfg.to2}
            className="c-ghost"
            onClick={e => { e.preventDefault(); onNavigate(cfg.to2); }}
          >{cfg.l2}</a>
        </div>
        <div className="c-status">
          <span className={`c-dot ${cfg.dotCls}`} aria-hidden="true"/>
          <span className="c-status-txt">{cfg.noteLabel}</span>
        </div>
      </div>
    </article>
  );
}

/* ══ MAIN ══ */
export default function Groupe() {
  const [visible, setVisible] = useState(false);
  const curRef  = useRef(null);
  const shRef   = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const t = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(t);
  }, []);

  useEffect(() => {
    const move = e => {
      if (!curRef.current) return;
      curRef.current.style.left = e.clientX + "px";
      curRef.current.style.top  = e.clientY + "px";
    };
    window.addEventListener("mousemove", move, { passive: true });
    return () => window.removeEventListener("mousemove", move);
  }, []);

  /* Transition de page douce */
  const handleNavigate = useCallback((to) => {
    if (shRef.current) shRef.current.classList.add("page-out");
    setTimeout(() => navigate(to), 280);
  }, [navigate]);

  return (
    <>
      <style>{CSS}</style>
      <div ref={curRef} className="cursor-glow" aria-hidden="true"/>
      <FloatingIcons />

      <div className="p" lang="fr">
        <div className="orb o1" aria-hidden="true"/>
        <div className="orb o2" aria-hidden="true"/>
        <div className="orb o3" aria-hidden="true"/>
        <div className="grid-bg" aria-hidden="true"/>

        <div ref={shRef} className={`sh${visible ? " in" : ""}`}>

          {/* ── Topbar ── */}
          <header className="tb">
            <div className="tb-left">
              <div className="tb-logo"><Logo/></div>
              <div>
                <div className="tb-name">Groupe HDC</div>
                 <div className="tb-sub">Hub Digital Consulting & Finance</div>
                
              </div>
            </div>
            <div className="tb-right">
              <div className="tb-stat">
                <span className="tb-stat-dot"/>
                +10 clients
              </div>
              <div className="tb-badge">
                <span className="tb-dot"/>
                Live
              </div>
            </div>
          </header>

          {/* ── Panel ── */}
          <main className="panel">
            <div className="panel-glow" aria-hidden="true"/>

            {/* Stats bar */}
            <div className="stats-bar" role="list">
              <div className="stat-item" role="listitem">
                <div className="stat-num ind"><Counter target={10} suffix="+"/></div>
                <div className="stat-label">Clients accompagnés</div>
              </div>
              <div className="stat-item" role="listitem">
                <div className="stat-num cyn"><Counter target={90} suffix="%"/></div>
                <div className="stat-label">Satisfaction</div>
              </div>
              <div className="stat-item" role="listitem">
                <div className="stat-num grn"><Counter target={30} suffix="+"/></div>
                <div className="stat-label">Apprenants</div>
              </div>
              <div className="stat-item" role="listitem">
                <div className="stat-num ind"><Counter target={5} suffix=" ans"/></div>
                <div className="stat-label">D'expérience</div>
              </div>
            </div>

            {/* Hero */}
            <div className="hero-top">
              <div className="eyebrow">
                <span className="ey-line"/>
                Choisissez votre espace
                <span className="ey-line r"/>
              </div>
              <h1 className="h1">
                Une plateforme,{" "}
                <span className="h1-grad">deux univers</span>
              </h1>
              <p className="lead">
                <b>Consulting</b> ou <b>Éducation</b> — accédez à votre espace en un clic.
              </p>
            </div>

            {/* Cards */}
            <div className="cards">
              <Card variant="consulting" onNavigate={handleNavigate}/>
              <Card variant="education"  onNavigate={handleNavigate}/>
            </div>

            {/* Footer nav */}
           {/* Footer nav */}
<nav className="fnav centered-footer" aria-label="Liens rapides">
  <span className="footer-text">Digital · Consulting · Éducation</span>
</nav>
          </main>

        </div>
      </div>
    </>
  );
}
